/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';

import { Text } from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks';

interface LaunchKpiPanelProps {
  kpis: {
    activeLaunches: number;
    totalAvailable: number;
    totalSold: number;
    totalUnits: number;
  };
}

/**
 * Agency-wide launch totals, shown to managers on desktop only.
 *
 * Not `KpiCard`: that component upper-cases its label, and titles in this
 * area are sentence case. Hidden on mobile because agency totals are not
 * what a broker standing in a sales stand came to the phone for — the
 * screen belongs to the launches themselves.
 */
export function LaunchKpiPanel({ kpis }: LaunchKpiPanelProps) {
  const { t } = useTranslation('features/launches');
  const isMobile = useIsMobile();

  if (isMobile) return null;

  const entries = [
    { label: t('list.kpis.active'), value: kpis.activeLaunches },
    { label: t('list.kpis.available'), value: kpis.totalAvailable },
    { label: t('list.kpis.sold'), value: kpis.totalSold },
    { label: t('list.kpis.totalUnits'), value: kpis.totalUnits },
  ];

  return (
    <div
      data-tour="launch-kpis"
      className="grid grid-cols-4 gap-[var(--space-4)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] px-[var(--space-5)] py-[var(--space-4)]"
    >
      {entries.map(({ label, value }) => (
        <div key={label} className="flex flex-col gap-[var(--space-1)]">
          <Text
            as="span"
            className="text-[length:var(--text-stat)] [font-weight:var(--font-semibold)] tabular-nums text-[var(--text-primary)]"
          >
            {value}
          </Text>
          <Text
            as="span"
            className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]"
          >
            {label}
          </Text>
        </div>
      ))}
    </div>
  );
}
